import { useCallback, useState } from 'react';
import { useAppSelector, useDebounce } from 'shared/hooks';
import { EResponseHeaders, ESnackbarStyle, VrUser, School } from 'shared/types';
import { 
  getVrUsers, 
  assignAdminTagToVrUser, 
  checkTagAssignVrUser 
} from 'services/api/vrUsersService';
import { openNotification } from 'utils/notification-utils';
import { PAGE_SIZE_FOR_VR_USERS } from 'shared/constants/pagination';

type Result = {
  vrUsers: VrUser[];
  fetchVrUsers: () => Promise<void>;
  fetchAssignAdminTag: (vrUserId: string) => Promise<void>;
  fetchTagAssignment: (adminId: string, vrUserId: string) => Promise<boolean>
  totalPages: number;
  totalVrUsers: number;
  isVrUsersLoading: boolean;
  setCurrentPage: (page: number) => void;
  currentPage: number;
  pageSize: number;
  setPageSize: (value: number) => void;
  setSortBy: (value: string) => void;
  sortBy: string;
  searchValue: string;
  setSearchValue: (value: string) => void;
  schoolId: string;
  setSchoolId: (value: string) => void;
  period: string;
  setPeriod: (value: string) => void;
  allSchools: School[];
  debouncedParams: string;
};

const useVrUsersData = (): Result => {
  const [vrUsers, setVrUsers] = useState<VrUser[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_FOR_VR_USERS);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalVrUsers, setTotalVrUsers] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [isVrUsersLoading, setIsVrUsersLoading] = useState<boolean>(true);

  const schools = useAppSelector((state): School[] => state.schools.allSchools);
  const [schoolId, setSchoolId] = useState<string>(schools?.length > 1 ? '' : schools[0]?.id || '');
  const [period, setPeriod] = useState<string>('');
  const debouncedParams = useDebounce(searchValue, 1000);

  const fetchVrUsers = useCallback(async (isEditMode?: boolean): Promise<void> => {
    if (!isEditMode) setIsVrUsersLoading(true);
    try {
      const orderBy = { orderBy: sortBy || 'email,DESC' };
      const response = await getVrUsers(
        currentPage,
        pageSize,
        orderBy,
        { 
          search: debouncedParams,
          schoolId: schoolId,
          ...(period ? { period } : {}),
        }
      );
      setVrUsers(response.data);
      setTotalPages(+response.headers[EResponseHeaders.TOTAL_PAGES]);
      setTotalVrUsers(+response.headers[EResponseHeaders.TOTAL_RECORDS]);
    } catch (e) {
      openNotification(ESnackbarStyle.ERROR, e.message);
    } finally {
      if (!isEditMode) setIsVrUsersLoading(false);
    }
  }, [currentPage, pageSize, debouncedParams, schoolId, sortBy, period]);

  const fetchAssignAdminTag = useCallback(async (vrUserId: string): Promise<void> => {
    try {
      await assignAdminTagToVrUser(vrUserId)
    } catch (e) {
      openNotification(ESnackbarStyle.ERROR, e.message);
    }
  }, [])

  const fetchTagAssignment = useCallback(async (adminId: string, vrUserId: string): Promise<boolean> => {
    return await checkTagAssignVrUser(vrUserId, adminId)
  }, [])

  return {
    vrUsers,
    fetchVrUsers,
    fetchAssignAdminTag,
    fetchTagAssignment,
    totalPages,
    totalVrUsers,
    isVrUsersLoading,
    setCurrentPage,
    currentPage,
    pageSize,
    setPageSize,
    setSortBy,
    sortBy,
    setSearchValue,
    searchValue,
    schoolId,
    setSchoolId,
    period,
    setPeriod,
    allSchools: schools,
    debouncedParams
  };
};

export default useVrUsersData;
